import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiUser, HiLockClosed } from 'react-icons/hi';
import { Button, Card, CardHeader, Input } from '../components/ui';
import { getCurrentUser, updateProfile, changePassword } from '../services/auth.api';
import { useAuthStore } from '../store/authStore';
import { getErrorMessage } from '../services/api';

export function Profile() {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((state) => state.updateUser);

  const { data: me } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
  });

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Current password required when the email is changed (FIX-04)
  const [emailPassword, setEmailPassword] = useState('');
  const emailChanged = !!me && email.trim().toLowerCase() !== (me.email ?? '').toLowerCase();

  useEffect(() => {
    if (me) {
      setFirstName(me.firstName ?? '');
      setLastName(me.lastName ?? '');
      setEmail(me.email ?? '');
      setPhone(me.phone ?? '');
      setAddress(me.address ?? '');
    }
  }, [me]);

  const profileMutation = useMutation({
    mutationFn: () =>
      updateProfile({
        firstName,
        lastName,
        email,
        phone,
        address,
        currentPassword: emailChanged ? emailPassword : undefined,
      }),
    onSuccess: () => {
      toast.success('Profil gespeichert');
      updateUser({ firstName, lastName, email });
      setEmailPassword('');
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const passwordMutation = useMutation({
    mutationFn: () => changePassword(currentPassword, newPassword),
    onSuccess: () => {
      toast.success('Passwort geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    passwordMutation.mutate();
  };

  return (
    <div>
      <CardHeader title="Mein Profil" description="Persönliche Daten und Passwort verwalten" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal data */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <HiUser className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900">Persönliche Daten</h3>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              profileMutation.mutate();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Vorname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <Input
                label="Nachname"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
            <Input
              type="email"
              label="E-Mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {emailChanged && (
              <Input
                type="password"
                label="Aktuelles Passwort (zur Bestätigung der E-Mail-Änderung)"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            )}
            <Input
              type="tel"
              label="Telefon"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="z. B. +49 170 1234567"
            />
            <Input
              label="Adresse"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Straße, PLZ Ort"
            />
            {me?.role && (
              <p className="text-xs text-gray-400">Rolle: {me.role}</p>
            )}
            <div className="flex justify-end pt-2">
              <Button type="submit" isLoading={profileMutation.isPending}>
                Speichern
              </Button>
            </div>
          </form>
        </Card>

        {/* Password change */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <HiLockClosed className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900">Passwort ändern</h3>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <Input
              type="password"
              label="Aktuelles Passwort"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <Input
              type="password"
              label="Neues Passwort"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <Input
              type="password"
              label="Neues Passwort bestätigen"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <p className="text-xs text-gray-500">
              Mindestens 8 Zeichen, mit Groß- und Kleinbuchstabe sowie einer Ziffer.
            </p>
            <div className="flex justify-end pt-2">
              <Button type="submit" isLoading={passwordMutation.isPending}>
                Passwort ändern
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
